import {
  AllocationLabels,
  BreakdownKey,
  KpiKey,
  MappingKey,
  ReportView,
} from "./types";

/** Fallback display names for the two allocation buckets. */
export const defaultAllocationLabels: AllocationLabels = {
  reportingDate: "Actual",
  budgetMonth: "Forecast",
};

export const kpiDefinitions: {
  key: KpiKey;
  label: string;
  footnote: string;
}[] = [
  { key: "revenue", label: "Revenue", footnote: "contributing source records" },
  {
    key: "cost",
    label: "Creator cost",
    footnote: "Blank amounts summed as zero",
  },
  {
    key: "profit",
    label: "Profit",
    footnote: "Revenue minus creator cost",
  },
  {
    key: "margin",
    label: "Margin",
    footnote: "Profit divided by revenue",
  },
];

export const breakdownOptions: { key: BreakdownKey; label: string }[] = [
  { key: "client", label: "Client" },
  { key: "manager", label: "Country Manager" },
  { key: "campaignName", label: "Campaign" },
  { key: "creatorName", label: "Creator" },
  { key: "market", label: "Market" },
  { key: "month", label: "Month" },
];

/**
 * Human labels for the mapping roles, used when telling the user which ones still need a
 * field picked.
 */
export const mappingLabels: Record<MappingKey, string> = {
  revenue: "Revenue",
  cost: "Creator cost",
  date: "Reporting date",
  budgetMonth: "Budget month",
  status: "Status field",
  client: "Client",
  manager: "Country Manager",
  campaign: "Campaign ID",
  campaignName: "Campaign name",
  creator: "Creator ID",
  creatorName: "Creator name",
  market: "Market",
  costType: "Cost type",
};

/**
 * A role's display label, with the workspace's own override (if any) taking precedence
 * over the built-in default - e.g. a workspace that calls its "manager" role "Deal Owner".
 */
export const getMappingLabel = (
  roleLabels: Partial<Record<MappingKey, string>> | undefined,
  role: MappingKey,
): string => roleLabels?.[role]?.trim() || mappingLabels[role];

/**
 * A standard breakdown option backed by a mapping role is only offered once the workspace
 * has actually mapped a field to it - otherwise it is not a real dimension of this
 * workspace's data (e.g. "Market" for a workspace whose records have no such concept at
 * all), and every record would group under "Unassigned" regardless of filter or breakdown
 * choice. "Month" has no mapping role - it is always computed from the allocation date - so
 * it is always offered.
 */
export const getBreakdownOptions = (configuration: {
  additionalDimensions: { key: BreakdownKey; label: string }[];
  roleLabels?: Partial<Record<MappingKey, string>>;
  mappings: Record<MappingKey, string>;
}): { key: BreakdownKey; label: string }[] => [
  ...breakdownOptions
    .filter(
      (option) =>
        !(option.key in mappingLabels) ||
        Boolean(configuration.mappings[option.key as MappingKey]),
    )
    .map((option) => {
      // Only an explicit override changes the label here. The admin-facing `mappingLabels`
      // wording ("Campaign name", "Creator name") is deliberately more verbose than this
      // dropdown's ("Campaign", "Creator"), so it must never be used as a silent fallback.
      const override =
        configuration.roleLabels?.[option.key as MappingKey]?.trim();
      return override ? { key: option.key, label: override } : option;
    }),
  ...configuration.additionalDimensions,
];

/**
 * Roles the report cannot produce numbers without. The rest only affect drill-down and
 * breakdown labels, so an unmapped one degrades the preview instead of breaking it.
 */
export const requiredMappings: MappingKey[] = ["revenue", "cost", "date"];

const today = new Date();
const currentMonth = `${today.getFullYear()}-${String(
  today.getMonth() + 1,
).padStart(2, "0")}`;

/**
 * The default view starts on the current calendar month. Viewer choices are persisted
 * locally per workspace rather than in the shared report configuration.
 */
export const defaultReportView: ReportView = {
  periods: [currentMonth],
  breakdown: "client",
  filters: {
    client: [],
    manager: [],
    campaignName: [],
    creatorName: [],
    market: [],
    month: [],
    customDimension1: [],
    customDimension2: [],
  },
  kpis: kpiDefinitions.map((kpi) => kpi.key),
};

/**
 * Identifiers the server uses for the columns that are not meta fields. Kept here so the
 * module does not scatter these string literals around.
 */
export const SYNTHETIC_FIELDS = {
  projectUuid: "ws_project_uuid",
  projectName: "ws_project_name",
  taskUuid: "ws_task_uuid",
  taskName: "ws_task_name",
  taskCreatedDate: "ws_task_created_date",
  globalTaskUuid: "ws_global_task_uuid",
} as const;
